import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {useSelector, useDispatch} from 'react-redux'
import _ from 'lodash'
import {RootState} from '../../redux/rootReducer'
import {
  actions as deviceLookupActions,
  DeviceTimelineEntryType,
  DeviceTransitionStatus,
} from '../../redux/modules/deviceLookup'
import AlertMessage from '../../components/AlertMessage'
import styles from './styles'
import {ModalPage} from 'nav/ModalPage'

const isValidDate = (value: Date): boolean => !isNaN(value.getTime())

const formatDate = (date: string | null): string => {
  if (!date) return '--'
  const parsed = new Date(date)
  return isValidDate(parsed) ? parsed.toISOString().split('T')[0] : '--'
}

const formatDateTime = (date: string | null): string => {
  if (!date) return '--'
  const parsed = new Date(date)
  return isValidDate(parsed) ? parsed.toISOString().replace('T', ' ').slice(0, 16) : '--'
}

const INVENTORY_STATUS_LABEL: Record<string, string> = {
  assigned: 'In Use',
  in_inventory: 'In Inventory',
}

// Matches AssignmentStatus in theragen-api (patient_device_assignment.ts) — that type is
// deliberately scoped to only the 3 states currently persisted anywhere (see
// DeviceLifecycleService's ALLOWED_DEVICE_LIFECYCLE_TRANSITIONS comment). Only 'active' has any
// outgoing transition, which is why the Actions section below only ever offers Replace/Mark
// Lost: 'suspended'/'returned' aren't real states yet, so those actions from the original E5-T3
// design are deferred to a future theragen-api ticket that would add them to the enum first.
const STATUS_CHIP_COLOR: Record<string, 'success' | 'info' | 'error' | 'default'> = {
  active: 'success',
  replaced: 'info',
  lost: 'error',
}

const DEVICE_TRANSITIONS: {
  toStatus: DeviceTransitionStatus
  label: string
  color?: 'error'
  dialogTitle: string
  confirmMessage: string
}[] = [
  {
    toStatus: 'replaced',
    label: 'Replace',
    dialogTitle: 'Replace device',
    confirmMessage:
      'Are you sure you want to mark this device as replaced? This ends its current assignment and cannot be undone.',
  },
  {
    toStatus: 'lost',
    label: 'Mark Lost',
    color: 'error',
    dialogTitle: 'Mark device lost',
    confirmMessage:
      'Are you sure you want to mark this device as lost? This ends its current assignment and cannot be undone. Optionally link a replacement device below, or leave both fields blank to assign one later.',
  },
]

// Not the full type-to-color mapping a future E5 (lifecycle) ticket might want — just enough to
// distinguish "device became available" from "device got assigned" at a glance in this timeline.
const TIMELINE_DOT_COLOR: Record<DeviceTimelineEntryType, string> = {
  inventory_added: 'primary.main',
  assignment: 'success.main',
  status_transition: 'warning.main',
}

const DeviceLookupPage: ModalPage<{udid: string}> = props => {
  const dispatch = useDispatch()
  const {udid, onClickCancel} = props
  const {
    detail,
    detailLoading,
    detailError,
    detailRequestUdid,
    timeline,
    timelineUdid,
    timelineLoading,
    timelineError,
    timelineRequestUdid,
    pendingToStatus,
    pendingUdid,
    transitionError,
    transitionErrorUdid,
  } = useSelector((state: RootState) => state.deviceLookup)

  useEffect(() => {
    dispatch(deviceLookupActions.getDetail({udid}))
    dispatch(deviceLookupActions.getTimeline({udid}))
  }, [dispatch, udid])

  // transitionDialogOpen drives the Dialog's `open` prop; transitionDialogFor drives its
  // content (title/message/labels). They're separate because MUI keeps the Dialog mounted
  // through its ~225ms exit animation — nulling transitionDialogFor as soon as the dialog
  // starts closing would blank that content while it's still visible. transitionDialogFor is
  // only cleared once the exit animation actually finishes (Dialog's onExited below).
  const [transitionDialogOpen, setTransitionDialogOpen] = useState(false)
  const [transitionDialogFor, setTransitionDialogFor] = useState<DeviceTransitionStatus | null>(
    null,
  )
  const [replacementUdid, setReplacementUdid] = useState('')
  const [replacementDeviceName, setReplacementDeviceName] = useState('')
  const [transitionValidationError, setTransitionValidationError] = useState<string | undefined>()

  // The transition dialog's open/text/validation state is local, not gated by isDetailCurrent the
  // way detail/timeline are — without this, reusing the modal for a different device (udid prop
  // change) while the dialog is open would leave it stuck open, and once the new device's detail
  // re-renders the Actions block, it would pop back open unprompted for the wrong device. Mirrors
  // the isSubmittingRef cross-device reset effect below.
  useEffect(() => {
    setTransitionDialogOpen(false)
    setTransitionDialogFor(null)
    setReplacementUdid('')
    setReplacementDeviceName('')
    setTransitionValidationError(undefined)
  }, [udid])

  // This component instance can be reused for a different device without unmounting (e.g. the
  // parent swaps `udid` while the modal stays open, or React reconciles two same-type elements
  // at the same position) — clear() only fires from the Close button, not from a udid change.
  // Without these checks, `detail`/`timeline` left over from the *previous* device would render
  // (Actions buttons included) while the new device's fetch is still in flight, and a click in
  // that window would fire a transition against the wrong id/udid. Kept as two separate checks,
  // matching the existing detail/timeline independence (a device can have a loaded timeline
  // with no/failed detail, or vice versa) — DeviceDetail already carries its own `udid`, but
  // DeviceTimelineEntry doesn't, hence the separate `timelineUdid` tracked in state for it.
  const isDetailCurrent = detail?.udid === udid
  const isTimelineCurrent = timelineUdid === udid
  // Same idea, for the loading/error fallback branches: detailLoading/detailError/
  // timelineLoading/timelineError aren't tagged with a udid the way detail/timeline data is, so
  // without this a stale loading/error state from the previous device could render attributed
  // to the new one for the one render between a udid prop change and that device's own
  // getDetail/getTimeline dispatch actually landing (set at dispatch time, not response time).
  const isDetailRequestCurrent = detailRequestUdid === udid
  const isTimelineRequestCurrent = timelineRequestUdid === udid
  // pendingToStatus/transitionError are a single shared scalar pair (not per-device like
  // detail/timeline), so a transition still in flight — or failed — for a device the modal has
  // since moved away from would otherwise disable/spin this device's own action buttons, or
  // surface the other device's error under this device's Actions section.
  const isPendingForThisDevice = pendingUdid === udid
  const isTransitionErrorForThisDevice = transitionErrorUdid === udid

  // Most-recent-first, matching how the timeline reads as a history of what happened to the
  // device — the API returns it chronologically ascending for easy appending server-side.
  const timelineDescending = useMemo(() => [...timeline].reverse(), [timeline])

  const activeTransition = DEVICE_TRANSITIONS.find(({toStatus}) => toStatus === transitionDialogFor)

  const closePage = () => {
    dispatch(deviceLookupActions.clear())
    onClickCancel()
  }

  // `disabled={pendingToStatus !== null}` below only takes effect on the next render commit,
  // which leaves a narrow window where a very fast double-click (or two different buttons
  // clicked in the same tick) could fire twice before React re-renders. This ref is checked
  // synchronously inside the click handler itself, closing that window regardless of render
  // timing — reset once pendingToStatus goes back to null (transition settled, either way).
  const isSubmittingRef = useRef(false)
  useEffect(() => {
    // Also reset when the in-flight transition belongs to a different device: without this, a
    // click on device A leaves this ref true for the lifetime of that component instance, and if
    // the modal gets reused for device B before A's transition settles, B's own click would be
    // silently swallowed by A's still-true ref (pendingToStatus alone doesn't null out until A's
    // saga finishes, regardless of which device is being viewed by then).
    if (pendingToStatus === null || !isPendingForThisDevice) {
      isSubmittingRef.current = false
    }
  }, [pendingToStatus, isPendingForThisDevice])

  // Takes the current device's name as an explicit param for the same reason confirmTransition
  // takes id/udid explicitly — this handler is defined outside the `detail.status === 'active'`
  // block, so `detail` isn't narrowed to non-undefined here the way it is at each call site.
  const openTransitionDialog = (toStatus: DeviceTransitionStatus, currentName: string) => {
    if (isSubmittingRef.current) return
    dispatch(deviceLookupActions.clearTransitionError())
    setReplacementUdid('')
    // Only 'replaced' pre-fills the name (a replacement is mandatory there); 'lost' defaults to
    // no replacement, so pre-filling just the name would leave it unpaired with a blank UDID.
    setReplacementDeviceName(toStatus === 'replaced' ? currentName : '')
    setTransitionValidationError(undefined)
    setTransitionDialogFor(toStatus)
    setTransitionDialogOpen(true)
  }

  const closeTransitionDialog = () => {
    setTransitionDialogOpen(false)
  }

  const confirmTransition = (id: string, udid: string) => {
    if (isSubmittingRef.current || !transitionDialogFor) return
    const trimmedUdid = _.trim(replacementUdid)
    const trimmedName = _.trim(replacementDeviceName)

    if (transitionDialogFor === 'replaced') {
      if (_.isEmpty(trimmedUdid)) {
        setTransitionValidationError('Replacement device UDID is required.')
        return
      }
      if (_.isEmpty(trimmedName)) {
        setTransitionValidationError('Replacement device name is required.')
        return
      }
    } else if (_.isEmpty(trimmedUdid) !== _.isEmpty(trimmedName)) {
      // Marking a device lost may optionally link a replacement in the same request, but a
      // half-filled pair can't be submitted — either both fields identify the replacement, or
      // neither does (mark lost with no replacement yet).
      setTransitionValidationError(
        'Enter both a replacement device UDID and name, or leave both blank.',
      )
      return
    }

    isSubmittingRef.current = true
    const toStatus = transitionDialogFor
    setTransitionDialogOpen(false)
    dispatch(
      deviceLookupActions.transition({
        id,
        udid,
        toStatus,
        ...(_.isEmpty(trimmedUdid) ? {} : {newDeviceUdid: trimmedUdid, newDeviceName: trimmedName}),
      }),
    )
  }

  return (
    <Container component="main">
      <Box sx={styles.modal}>
        <IconButton aria-label="Close device lookup modal" onClick={closePage} sx={styles.closeButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={styles.scrollableContent}>
          <Typography variant="h4" sx={styles.headerLabel}>
            Device Lookup
          </Typography>

          {detail && isDetailCurrent ? (
            <>
              <Box sx={styles.serialRow}>
                <Typography variant="h5" sx={styles.serialValue}>
                  {detail.udid}
                </Typography>
                <Chip
                  label={detail.status ? _.capitalize(detail.status) : 'Unassigned'}
                  color={detail.status ? STATUS_CHIP_COLOR[detail.status] ?? 'default' : 'default'}
                />
              </Box>
              <Box sx={styles.statRow}>
                <Box sx={styles.statTile}>
                  <Typography sx={styles.statTileValue}>
                    {detail.assignedPatient
                      ? `${detail.assignedPatient.lastName}, ${detail.assignedPatient.firstName}`
                      : 'Unassigned'}
                  </Typography>
                  <Typography variant="body2" sx={styles.statTileLabel}>
                    Assigned patient
                  </Typography>
                </Box>
                <Box sx={styles.statTile}>
                  <Typography sx={styles.statTileValue}>
                    {INVENTORY_STATUS_LABEL[detail.inventoryStatus] ?? detail.inventoryStatus}
                  </Typography>
                  <Typography variant="body2" sx={styles.statTileLabel}>
                    Inventory status
                  </Typography>
                </Box>
                <Box sx={styles.statTile}>
                  <Typography sx={styles.statTileValue}>{formatDate(detail.assignedDate)}</Typography>
                  <Typography variant="body2" sx={styles.statTileLabel}>
                    Assigned date
                  </Typography>
                </Box>
                <Box sx={styles.statTile}>
                  <Typography sx={styles.statTileValue}>{formatDateTime(detail.lastSync)}</Typography>
                  <Typography variant="body2" sx={styles.statTileLabel}>
                    Last sync
                  </Typography>
                </Box>
              </Box>
              {/* A failed background refetch (e.g. after a successful transition) shouldn't blank
                  out detail we already have — surface it as a banner instead of replacing the
                  panel with a bare error line the way the very first load does below. */}
              {detailError && (
                <AlertMessage error={detailError} severity="error" sx={styles.refetchErrorBanner} />
              )}
              {detail.status === 'active' && (
                <>
                  <Typography variant="h6" sx={styles.sectionLabel}>
                    Actions
                  </Typography>
                  {transitionError && isTransitionErrorForThisDevice && (
                    <AlertMessage
                      error={transitionError}
                      severity="error"
                      sx={styles.transitionErrorBanner}
                    />
                  )}
                  <Box sx={styles.actionsRow}>
                    {DEVICE_TRANSITIONS.map(({toStatus, label, color}) => {
                      const isPending = isPendingForThisDevice && pendingToStatus === toStatus
                      return (
                        <Button
                          key={toStatus}
                          variant="contained"
                          color={color}
                          disabled={pendingToStatus !== null && isPendingForThisDevice}
                          onClick={() => openTransitionDialog(toStatus, detail.name)}>
                          {label}
                          {isPending && (
                            <CircularProgress size={16} sx={styles.actionButtonProgress} />
                          )}
                        </Button>
                      )
                    })}
                  </Box>
                  <Dialog
                    open={transitionDialogOpen}
                    onClose={closeTransitionDialog}
                    TransitionProps={{onExited: () => setTransitionDialogFor(null)}}>
                    <DialogTitle>{activeTransition?.dialogTitle}</DialogTitle>
                    <DialogContent>
                      <DialogContentText>{activeTransition?.confirmMessage}</DialogContentText>
                      {transitionValidationError && (
                        <AlertMessage
                          error={transitionValidationError}
                          severity="error"
                          sx={styles.transitionDialogError}
                        />
                      )}
                      <TextField
                        autoFocus
                        fullWidth
                        label={
                          transitionDialogFor === 'replaced'
                            ? 'Replacement device UDID'
                            : 'Replacement device UDID (optional)'
                        }
                        value={replacementUdid}
                        onChange={e => setReplacementUdid(e.currentTarget.value)}
                        sx={styles.transitionDialogField}
                      />
                      <TextField
                        fullWidth
                        label={
                          transitionDialogFor === 'replaced'
                            ? 'Replacement device name'
                            : 'Replacement device name (optional)'
                        }
                        value={replacementDeviceName}
                        onChange={e => setReplacementDeviceName(e.currentTarget.value)}
                        sx={styles.transitionDialogField}
                      />
                    </DialogContent>
                    <DialogActions sx={styles.transitionDialogActions}>
                      <Button onClick={closeTransitionDialog}>Cancel</Button>
                      <Button
                        variant="contained"
                        onClick={() => confirmTransition(detail.id, detail.udid)}>
                        Confirm
                      </Button>
                    </DialogActions>
                  </Dialog>
                </>
              )}
            </>
          ) : detailLoading && isDetailRequestCurrent ? (
            <Typography>Loading...</Typography>
          ) : detailError && isDetailRequestCurrent ? (
            <Typography color="error">{detailError}</Typography>
          ) : null}

          <Typography variant="h6" sx={styles.sectionLabel}>
            Device timeline
          </Typography>
          {timelineDescending.length > 0 && isTimelineCurrent ? (
            <>
              <Box>
                {timelineDescending.map((entry, index) => (
                  <Box key={index} sx={styles.timelineEntry}>
                    <Box sx={styles.timelineRail}>
                      <Box
                        sx={{...styles.timelineDot, backgroundColor: TIMELINE_DOT_COLOR[entry.type]}}
                      />
                      {index < timelineDescending.length - 1 && <Box sx={styles.timelineLine} />}
                    </Box>
                    <Box sx={styles.timelineContent}>
                      <Typography variant="body2" sx={styles.timelineDate}>
                        {formatDate(entry.date)}
                      </Typography>
                      <Typography sx={styles.timelineDescription}>{entry.description}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {timelineError && <AlertMessage error={timelineError} severity="error" />}
            </>
          ) : timelineLoading && isTimelineRequestCurrent ? (
            <Typography>Loading...</Typography>
          ) : timelineError && isTimelineRequestCurrent ? (
            <Typography color="error">{timelineError}</Typography>
          ) : (
            <Typography variant="body2">No events yet.</Typography>
          )}
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button onClick={closePage} variant="contained">
            Close
          </Button>
        </Box>
      </Box>
    </Container>
  )
}

export default DeviceLookupPage
